import React, { useState, useEffect } from 'react';

const MembershipPage = ({
  API,
  isDarkMode,
  userToken,
  setCurrentView,
  setInitialProfileTab
}) => {
  const [plans, setPlans] = useState([]);
  const [disclaimer, setDisclaimer] = useState({ enabled: false, text: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!API) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [plansRes, disclaimerRes] = await Promise.all([
          fetch(`${API}/membership/plans`),
          fetch(`${API}/membership/payment-disclaimer`)
        ]);
        if (plansRes.ok) {
          const data = await plansRes.json();
          // Sort: free first, then by price ascending
          data.sort((a, b) => {
            if (a.name === 'free') return -1;
            if (b.name === 'free') return 1;
            return (a.price || 0) - (b.price || 0);
          });
          setPlans(data);
        }
        if (disclaimerRes.ok) {
          setDisclaimer(await disclaimerRes.json());
        }
      } catch (error) {
        console.error('Error fetching membership data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API]);

  const handleUpgrade = () => {
    setInitialProfileTab('membership');
    setCurrentView('profile');
  };

  const cardClass = `rounded-2xl border ${isDarkMode ? 'bg-gray-800 border-gray-700/60' : 'bg-white border-gray-200/80'} shadow-sm`;

  // Get features to display for a plan
  const getPlanFeatures = (plan) => {
    // Prefer display_features if admin has configured them
    if (plan.display_features && plan.display_features.length > 0) {
      return plan.display_features;
    }
    // Fall back to custom_features as simple enabled items
    if (plan.custom_features && plan.custom_features.length > 0) {
      return plan.custom_features.map(f => ({ label: f, enabled: true }));
    }
    // Fall back to features object
    const featureLabels = {
      translations_per_day: (v) => v === -1 ? 'Unlimited translations' : `${v} translations/day`,
      audio_enabled: () => 'Audio pronunciation',
      favorites_enabled: () => 'Save favorites',
      daily_words_enabled: () => 'Daily learning words',
      progress_tracking_enabled: () => 'Progress tracking & calendar',
      voice_input_enabled: () => 'Voice input',
      categories_enabled: () => 'Category browsing',
      games_access: () => 'Learning games access',
      travel_tools_access: () => 'Travel Tools feature',
      conversation_challenge_access: () => 'Conversation challenges',
      request_word_enabled: () => 'Request new words',
    };
    const result = [];
    if (plan.features) {
      for (const [key, value] of Object.entries(plan.features)) {
        if (featureLabels[key]) {
          if (key === 'translations_per_day') {
            result.push({ label: featureLabels[key](value), enabled: true });
          } else {
            result.push({ label: featureLabels[key](), enabled: !!value });
          }
        }
      }
    }
    return result;
  };

  const getBillingLabel = (plan) => {
    if (plan.name === 'free' || plan.price === 0) return 'forever';
    if (plan.billing_period === 'monthly') return 'month';
    if (plan.billing_period === 'yearly') return 'year';
    if (plan.billing_period === 'once') return 'one-time';
    return plan.billing_period || 'month';
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className={`min-h-screen pb-24 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`sticky top-0 z-10 backdrop-blur-md ${isDarkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'} border-b`}>
          <div className="max-w-5xl mx-auto px-4 py-3">
            {setCurrentView && (
              <button onClick={() => setCurrentView('translator')} className={`flex items-center gap-1 mb-2 px-2 py-1 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                back
              </button>
            )}
            <div className="flex items-center gap-3">
              <span className="text-2xl">💎</span>
              <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Membership</h1>
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 py-10">
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2].map(i => (
              <div key={i} className={`${cardClass} p-8 animate-pulse`}>
                <div className={`h-6 w-24 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <div className={`h-10 w-32 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <div className="space-y-3">
                  {[1,2,3,4].map(j => <div key={j} className={`h-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-24 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Sticky Header */}
      <div className={`sticky top-0 z-10 backdrop-blur-md ${isDarkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'} border-b`}>
        <div className="max-w-5xl mx-auto px-4 py-3">
          {setCurrentView && (
            <button
              onClick={() => setCurrentView('translator')}
              className={`flex items-center gap-1 mb-2 px-2 py-1 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              back
            </button>
          )}
          <div className="flex items-center gap-3">
            <span className="text-2xl">💎</span>
            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Membership</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-8">

        {/* Hero Section */}
        <div className="text-center py-6">
          <h2 className={`text-3xl md:text-4xl font-extrabold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Choose Your Learning Path
          </h2>
          <p className={`text-base md:text-lg max-w-xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Unlock the full power of DarijaPro and learn Moroccan Darija faster
          </p>
        </div>

        {/* Plan Cards */}
        <div className={`grid gap-6 ${plans.length === 1 ? 'max-w-md mx-auto' : plans.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : plans.length >= 3 ? 'md:grid-cols-3 max-w-5xl mx-auto' : ''}`}>
          {plans.map((plan) => {
            const isFree = plan.name === 'free' || plan.price === 0;
            const isPopular = plan.is_popular;
            const features = getPlanFeatures(plan);
            const enabledFeatures = features.filter(f => f.enabled);
            const disabledFeatures = features.filter(f => !f.enabled);

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 ${
                  isPopular
                    ? isDarkMode
                      ? 'bg-gradient-to-b from-purple-900/50 via-gray-800 to-gray-800 border-2 border-purple-500/60 shadow-xl shadow-purple-500/10 hover:shadow-2xl hover:shadow-purple-500/20'
                      : 'bg-gradient-to-b from-purple-50 via-white to-white border-2 border-purple-400 shadow-xl shadow-purple-200/50 hover:shadow-2xl hover:shadow-purple-300/50'
                    : isDarkMode
                      ? 'bg-gray-800 border border-gray-700/60 shadow-sm hover:shadow-lg hover:border-gray-600'
                      : 'bg-white border border-gray-200/80 shadow-sm hover:shadow-lg hover:border-gray-300'
                } flex flex-col`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white text-center py-2 text-xs font-bold tracking-widest uppercase">
                    Most Popular
                  </div>
                )}

                <div className="p-6 md:p-8 flex flex-col flex-1">
                  {/* Plan Icon & Name */}
                  <div className="mb-5">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3 ${
                      isPopular
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                        : isFree
                          ? isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                          : isDarkMode ? 'bg-blue-900/50' : 'bg-blue-50'
                    }`}>
                      {isPopular ? (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                      ) : isFree ? (
                        <svg className={`w-6 h-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      ) : (
                        <svg className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )}
                    </div>
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {plan.display_name || plan.name}
                    </h3>
                    {plan.description && (
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {plan.description}
                      </p>
                    )}
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className={`text-4xl font-extrabold ${
                        isPopular
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent'
                          : isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {isFree ? 'Free' : `$${plan.price}`}
                      </span>
                      {!isFree && (
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          /{getBillingLabel(plan)}
                        </span>
                      )}
                    </div>
                    {isFree && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No credit card required</p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className={`border-t mb-5 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`} />

                  {/* Features List */}
                  <div className="space-y-3 flex-1">
                    {enabledFeatures.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                          isPopular
                            ? 'bg-purple-100 dark:bg-purple-900/50'
                            : isDarkMode ? 'bg-emerald-900/40' : 'bg-emerald-50'
                        }`}>
                          <svg className={`w-3 h-3 ${isPopular ? 'text-purple-600 dark:text-purple-400' : 'text-emerald-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature.label}
                        </span>
                      </div>
                    ))}
                    {disabledFeatures.map((feature, i) => (
                      <div key={`d-${i}`} className="flex items-start gap-2.5 opacity-50">
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <svg className={`w-3 h-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                        <span className={`text-sm line-through ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                          {feature.label}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <div className="mt-6">
                    {isFree ? (
                      <button
                        onClick={() => { if (!userToken) setCurrentView('profile'); }}
                        className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all ${
                          isDarkMode
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {userToken ? 'Current Plan' : 'Get Started Free'}
                      </button>
                    ) : (
                      <button
                        onClick={handleUpgrade}
                        className={`w-full py-3.5 rounded-xl font-bold text-sm text-white transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg ${
                          isPopular
                            ? 'bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 hover:from-purple-700 hover:via-pink-600 hover:to-orange-600 shadow-purple-500/25'
                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/25'
                        }`}
                      >
                        Upgrade Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* No plans fallback */}
        {plans.length === 0 && !loading && (
          <div className={`${cardClass} p-10 text-center`}>
            <div className="text-4xl mb-3">📋</div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No membership plans available right now. Check back later!
            </p>
          </div>
        )}

        {/* Why Upgrade Section */}
        {plans.some(p => p.price > 0) && (
          <div className={`${cardClass} p-6 md:p-8`}>
            <h3 className={`text-lg font-bold text-center mb-6 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Why Upgrade?
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: '🚀', title: 'Learn Faster', desc: 'Unlimited practice with no daily limits' },
                { icon: '🎤', title: 'Hear It', desc: 'Audio pronunciation for every word' },
                { icon: '📅', title: 'Track Progress', desc: 'Daily goals & learning calendar' },
                { icon: '🎮', title: 'Play & Learn', desc: 'Access all interactive games' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <h4 className={`text-sm font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{item.title}</h4>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Disclaimer */}
        {disclaimer.enabled && disclaimer.text && (
          <div className={`flex items-start gap-3 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-blue-50/60 border border-blue-100'}`}>
            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'}`}>
              <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h4 className={`text-xs font-bold mb-0.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                Secure Payments
              </h4>
              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {disclaimer.text}
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default MembershipPage;
